import React, { useState, useEffect, useRef } from 'react';
import { ctxGeo } from 'utils/brijContext';
import { useMask } from '@react-input/mask';
import { useTranslation } from 'react-i18next';
import { CountryCodeType, countryPhoneCodes } from 'types/Misc';
import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { ReactComponent as ExpandIcon } from 'assets/icons/svg/expand.svg';
import useElementSize from 'hooks/useElementSize';
import useWindowSize from 'hooks/useWindowSize';
import Dropdown from './Dropdown';

type SearchBarProps = {
  phoneNumber: string | undefined;
  setPhoneNumber: (phoneNumber: string) => void;
  existingPhoneNumber?: string;
  bottomOffset: number;
  placeholder: string;
  disabled?: boolean;
  autoFocus?: boolean;
};

const inputMaskFormatter = (numberLength: number) => {
  if (numberLength < 1) return '';
  const numberString = '_'.repeat(numberLength);
  let formattedNumber = '';
  let remainingLength = numberLength;
  if (remainingLength >= 3) {
    formattedNumber += numberString.slice(0, 3) + ' ';
    remainingLength -= 3;
  } else {
    formattedNumber += numberString.slice(0, remainingLength);
    remainingLength = 0;
  }
  if (remainingLength >= 3) {
    formattedNumber += numberString.slice(3, 6) + ' ';
    remainingLength -= 3;
  } else if (remainingLength > 0) {
    formattedNumber += numberString.slice(3, 3 + remainingLength);
    remainingLength = 0;
  }
  if (remainingLength > 0)
    formattedNumber += numberString.slice(-remainingLength);
  return formattedNumber.trim();
};

const SearchBar: React.FC<SearchBarProps> = ({
  bottomOffset,
  placeholder,
  disabled,
  phoneNumber,
  setPhoneNumber,
  existingPhoneNumber,
  autoFocus = false,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [bottomSpace, setBottomSpace] = useState<number | null>(null);
  const [dropdownSafeArea, setDropdownSafeArea] = useState<number>(0);
  const [openedInverted, setOpenedInverted] = useState<string>('bottom');
  const [codeChanged, setCodeChanged] = useState<boolean>(false);
  const [borderRadius, setBorderRadius] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<CountryCodeType>(
    countryPhoneCodes[0]
  );
  const [dropdownRef, { height }] = useElementSize();
  const { inputStyle } = useThemeContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const { t } = useTranslation('translation', {
    keyPrefix: 'phoneInput',
  });

  // Calculate mask for useMask hook
  const phoneMask = `${countryCode.mask} ${inputMaskFormatter(
    countryCode.numberLength
  )}`;

  const inputRef = useMask({
    mask: phoneMask,
    replacement: { _: /\d/ },
    showMask: true,
    separate: true,
  });

  useEffect(() => {
    const countryCodeFromContext: string = ctxGeo('country_code') || '';
    if (existingPhoneNumber) {
      const index = countryPhoneCodes.findIndex(
        (country) => country.countryCode === countryCodeFromContext
      );
      const nativeCountryCode = countryPhoneCodes[index];
      if (
        index !== -1 &&
        nativeCountryCode.mask.replace(/[\s()-]/g, '') ===
          existingPhoneNumber.slice(
            0,
            nativeCountryCode.mask.replace(/[\s()-]/g, '').length
          )
      )
        setCountryCode(countryPhoneCodes[index]);
    } else if (!existingPhoneNumber && !codeChanged) {
      if (countryCode) {
        const index = countryPhoneCodes.findIndex(
          (country) => country.countryCode === countryCodeFromContext
        );
        if (index !== -1) setCountryCode(countryPhoneCodes[index]);
      }
    }
  }, [existingPhoneNumber, setPhoneNumber]);

  useEffect(() => {
    if (bottomSpace) {
      if (height >= 82) setDropdownSafeArea(height);
      else setDropdownSafeArea(186);
    }
  }, [height, bottomSpace]);

  useEffect(() => {
    if (phoneNumber) setIsFocused(true);
    else setIsFocused(false);
  }, [phoneNumber]);

  // Initialize phone number with country code mask when empty
  useEffect(() => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      const fullMask = `${countryCode.mask} ${inputMaskFormatter(
        countryCode.numberLength
      )}`;
      setPhoneNumber(fullMask);
    }
  }, [phoneNumber, countryCode.mask, setPhoneNumber]);

  useEffect(() => {
    const scrollHandler = () => {
      if (contentRef.current && windowSize.height) {
        setBottomSpace(
          windowSize.height -
            contentRef.current.getBoundingClientRect().y -
            bottomOffset -
            58
        );
      }
    };
    if (bottomSpace) window.addEventListener('scroll', scrollHandler, true);
    else window.addEventListener('scroll', scrollHandler, true);

    return () => window.removeEventListener('scroll', scrollHandler, true);
  }, [bottomSpace, height, bottomOffset, windowSize.height]);

  useEffect(() => {
    setTimeout(() => {
      if (windowSize.height && contentRef.current) {
        setBottomSpace(
          windowSize.height -
            contentRef.current.getBoundingClientRect().y -
            bottomOffset -
            58
        );
      }
    }, 500);
  }, [windowSize, contentRef, height, bottomOffset]);

  useEffect(() => {
    if (bottomSpace) {
      if (dropdownOpen)
        setOpenedInverted(bottomSpace > dropdownSafeArea ? 'bottom' : 'top');
      else
        setOpenedInverted(
          openedInverted === 'bottom'
            ? bottomSpace < dropdownSafeArea
              ? 'top'
              : 'bottom'
            : bottomSpace > dropdownSafeArea
            ? 'bottom'
            : 'top'
        );
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (inputStyle === CornerStyle.SQUARE_CORNERS) setBorderRadius('0rem');
    else if (inputStyle === CornerStyle.ROUNDED_CORNERS)
      setBorderRadius('0.625rem');
    else setBorderRadius('1.625rem');
  }, [inputStyle]);

  // Autofocus the input when component mounts
  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      setTimeout(() => {
        inputRef.current?.focus();
        // Set cursor position to right after country code
        const cursorPosition = countryCode.mask.length + 1;
        inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  const focusInput = () => inputRef.current && inputRef.current.focus();
  const blurInput = () => inputRef.current && inputRef.current.blur();

  const getDropdownStyle = (openedInverted: string) => {
    let dropdownStyle =
      openedInverted === 'bottom'
        ? {
            borderTopLeftRadius:
              bottomSpace && bottomSpace < dropdownSafeArea
                ? borderRadius
                : '0',
            borderBottomLeftRadius:
              bottomSpace && bottomSpace > dropdownSafeArea
                ? borderRadius
                : '0',
            borderTopRightRadius:
              bottomSpace && bottomSpace < dropdownSafeArea
                ? borderRadius
                : '0',
            borderBottomRightRadius:
              bottomSpace && bottomSpace > dropdownSafeArea
                ? borderRadius
                : '0',
            top:
              bottomSpace && bottomSpace > dropdownSafeArea ? '57px' : `-180px`,
          }
        : {
            borderTopLeftRadius:
              bottomSpace && bottomSpace < dropdownSafeArea
                ? borderRadius
                : '0',
            borderBottomLeftRadius:
              bottomSpace && bottomSpace > dropdownSafeArea
                ? borderRadius
                : '0',
            borderTopRightRadius:
              bottomSpace && bottomSpace < dropdownSafeArea
                ? borderRadius
                : '0',
            borderBottomRightRadius:
              bottomSpace && bottomSpace > dropdownSafeArea
                ? borderRadius
                : '0',
            bottom:
              bottomSpace && bottomSpace > dropdownSafeArea ? `-180px` : '58px',
          };
    return dropdownStyle;
  };

  return (
    <>
      {dropdownOpen && (
        <div
          className='z-40 fixed top-0 left-0 w-full h-full'
          onClick={() => setDropdownOpen(false)}
        />
      )}
      <div ref={contentRef} className='z-auto relative w-full'>
        {bottomSpace && (
          <button
            type='button'
            aria-label={t('ariaChangeCountryCode')}
            className={`absolute top-0 left-6 bottom-0 h-8 flex flex-row items-center m-auto cursor-pointer overflow-hidden focus-visible:outline focus-visible:outline-black/60 
              ${
                disabled
                  ? 'pointer-events-none opacity-50'
                  : 'pointer-events-auto opacity-100'
              }
            `}
            onClick={() => {
              dropdownOpen && blurInput();
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <label className='text-2xl cursor-pointer'>
              {countryCode.flag}
            </label>
            <ExpandIcon
              fill='#000000'
              className='duration-500 opacity-70'
              style={
                bottomSpace
                  ? {
                      transform: dropdownOpen
                        ? bottomSpace > dropdownSafeArea
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)'
                        : bottomSpace > dropdownSafeArea
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                    }
                  : {}
              }
            />
          </button>
        )}
        <div className='w-full'>
          <input
            type='tel'
            ref={inputRef}
            value={phoneNumber}
            disabled={disabled}
            autoFocus={autoFocus}
            name='phone-number-input'
            onChange={(event) => setPhoneNumber(event.target.value)}
            onFocus={(event) => {
              setDropdownOpen(false);
              setIsFocused(true);
              // Set cursor position to right after country code
              setTimeout(() => {
                const cursorPosition = countryCode.mask.length + 1;
                event.target.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            onClick={(event) => {
              // Ensure cursor stays at or after the country code
              const cursorPosition = countryCode.mask.length + 1;
              if (event.currentTarget.selectionStart! < cursorPosition) {
                event.currentTarget.setSelectionRange(
                  cursorPosition,
                  cursorPosition
                );
              }
            }}
            style={{
              ...{ border: '2px solid #e7eaeb' },
              ...{
                borderRadius: borderRadius,
                borderTopLeftRadius: dropdownOpen
                  ? bottomSpace && bottomSpace < dropdownSafeArea
                    ? '0'
                    : borderRadius
                  : borderRadius,
                borderTopRightRadius: dropdownOpen
                  ? bottomSpace && bottomSpace < dropdownSafeArea
                    ? '0'
                    : borderRadius
                  : borderRadius,
                borderBottomLeftRadius: dropdownOpen
                  ? bottomSpace && bottomSpace < dropdownSafeArea
                    ? borderRadius
                    : '0'
                  : borderRadius,
                borderBottomRightRadius: dropdownOpen
                  ? bottomSpace && bottomSpace < dropdownSafeArea
                    ? borderRadius
                    : '0'
                  : borderRadius,
              },
            }}
            className={`w-full h-searchBar pl-[72px] pr-6 my-1.5 tracking-[0.1px] duration-300 placeholder:opacity-70
            ${dropdownOpen || phoneNumber ? 'bg-transparent' : 'bg-lightgray'}`}
          />
          <span
            className={`absolute z-auto left-[76px] top-[21px] w-max max-w-[calc(100%_-_100px)] text-muted transition-all duration-300 pointer-events-none whitespace-nowrap text-ellipsis overflow-hidden
            ${dropdownOpen ? 'delay-100' : 'delay-0'}
            ${
              isFocused
                ? 'text-xxxs px-0.5 bg-white -translate-x-14 -translate-y-5 opacity-100'
                : 'text-[0.9rem] bg-transparent translate-x-0 translate-y-0 opacity-80'
            }
          `}
          >
            {placeholder}
          </span>
        </div>
        <div
          ref={dropdownRef}
          style={{
            ...{
              height: dropdownOpen ? `186px` : '0px',
              background: '#FFFFFF',
            },
            ...getDropdownStyle(openedInverted),
          }}
          className={`z-50 absolute duration-300 shadow-dropdown 
          ${
            dropdownOpen
              ? 'w-[calc(100%_-_0.125rem)] ml-px left-0 overflow-auto'
              : 'w-[calc(100%_-_3.25rem)] left-[1.625rem] overflow-hidden'
          }`}
        >
          <Dropdown
            options={countryPhoneCodes}
            borderRadius={borderRadius}
            onSelect={(countryCode: CountryCodeType) => {
              setPhoneNumber('');
              setCountryCode(countryCode);
              setDropdownOpen(false);
              setIsFocused(true);
              setCodeChanged(true);
              setTimeout(() => focusInput(), 300);
            }}
            invertBorders={
              bottomSpace && bottomSpace < dropdownSafeArea ? true : false
            }
          />
        </div>
      </div>
    </>
  );
};

export default SearchBar;
